import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useLanguage from "../../hooks/useLanguage.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import {
  trackCTA,
  trackSectionViewed,
  useSectionOnceObserver,
} from "../../lib/analytics";
import { buildDemoInvitationUrl } from "../../lib/demoInvitation.js";
import { DWI_SECTION } from "./sections.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * Closing statement.
 *
 * Two actions, in the order a visitor from search actually wants them: the
 * price, and — for whoever is not ready to look at a price — the sample
 * invitation, which is the cheapest possible next step and the one thing on the
 * page that answers "is it any good?" without a purchase.
 *
 * `trackLead` is deliberately not fired here. Lead is reserved for CTAs that
 * carry real signup intent (destination is the create-event flow); a scroll to
 * the plans and a preview of a demo are both CTA clicks and nothing more.
 */
const InvitationFinalCta = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const { shortLanguage } = useLanguage();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(DWI_SECTION.FINAL_CTA, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section wps-section wps-final">
      <div className="container">
        <div className="wps-final-inner">
          <h2 className="heading-136">
            {t("digitalWeddingInvitation.finalCta.heading")}
          </h2>
          <p className="body-20 wps-final-body">
            {t("digitalWeddingInvitation.finalCta.body")}
          </p>
          <Button
            href={localize("/#pricing")}
            text={t("digitalWeddingInvitation.finalCta.ctaPrimary")}
            className="button-lg"
            onClick={(e) => {
              trackCTA("digital_wedding_invitation_final_cta", {
                ...analyticsContext,
                ctaName: "see_plans",
              });
              scrollToPlans(e);
            }}
          />
          <p className="dwi-final-secondary">
            <a
              className="wps-text-link"
              href={buildDemoInvitationUrl(shortLanguage)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackCTA("digital_wedding_invitation_final_cta", {
                  ...analyticsContext,
                  ctaName: "view_demo",
                })
              }
            >
              {t("digitalWeddingInvitation.finalCta.ctaSecondary")}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default InvitationFinalCta;
